// Shared milk-option vocabulary — the "milk lineup" a menu template / menu
// advertises (stored as `MenuTemplate.milkOptionCodes` and
// `Menu.settings.milkOptionCodes`). These are free-form string CODES, not a DB
// enum; `chocolate` vs `regular` is encoded in the code suffix and its human
// label. The canonical list + labels were historically duplicated by hand in
// `apps/menus/server/src/managers/menu-builder.ts` and the client
// `template-configuration-form.tsx`; this is the shared source both the pricing
// matrix (schema) and the pricing tab (client) consume so milk-line pricing can
// never drift from the milk selector.
//
// Sizes live in the LABEL, not the code — a code only needs a size suffix when
// the same variety is served at two sizes (today: 1% Lactose Free and Whole
// Milk, each at 8 oz for K-12 cartons and 6 oz for preschool/CACFP bulk
// service). Each code maps to exactly ONE live catalog component
// (`Component.milkOptionCode`), so two sizes of the same variety require two
// codes to carry two SKUs.

/** The canonical, ordered set of standard milk option codes. */
export const CANONICAL_MILK_OPTION_CODES = [
  '0_REGULAR',
  '1_REGULAR',
  '0_CHOCOLATE',
  '1_CHOCOLATE',
  '1_LACTOSE_FREE_8OZ',
  '1_LACTOSE_FREE_6OZ',
  'WHOLE_MILK_8OZ',
  'WHOLE_MILK_6OZ',
  'OAT_8OZ',
  'SOY_8OZ',
  'SOY_6OZ',
  'ALMOND_8OZ',
  'PEA_8OZ',
] as const

export type CanonicalMilkOptionCode = (typeof CANONICAL_MILK_OPTION_CODES)[number]

/**
 * Legacy code spellings → their canonical form. Carries the reversed alias
 * codes (`REGULAR_0` etc.) that some importers emit, plus the retired
 * `1_LACTAID` vocabulary (renamed 2026-07 to Lactose Free; the 8 oz carton is
 * the historical meaning) and the retired size-less `WHOLE_MILK` (labeled
 * "Whole Milk (6 oz)" when the lineup had a single whole-milk size — the
 * 6 oz is its historical meaning). Data migrations rewrote stored rows, but
 * read paths stay tolerant of stragglers in JSON blobs / exports.
 */
export const LEGACY_MILK_OPTION_CODE_ALIASES: Record<string, CanonicalMilkOptionCode> = {
  REGULAR_0: '0_REGULAR',
  REGULAR_1: '1_REGULAR',
  CHOCOLATE_0: '0_CHOCOLATE',
  CHOCOLATE_1: '1_CHOCOLATE',
  '1_LACTAID': '1_LACTOSE_FREE_8OZ',
  LACTAID_1: '1_LACTOSE_FREE_8OZ',
  WHOLE_MILK: 'WHOLE_MILK_6OZ',
}

/** True when `value` is one of the canonical milk option codes. */
export function isCanonicalMilkOptionCode(value: unknown): value is CanonicalMilkOptionCode {
  return typeof value === 'string' && (CANONICAL_MILK_OPTION_CODES as readonly string[]).includes(value)
}

/**
 * Canonical form of a milk option code: canonical codes pass through, legacy
 * aliases collapse to their canonical form, anything else returns null.
 */
export function normalizeMilkOptionCode(value: unknown): CanonicalMilkOptionCode | null {
  if (isCanonicalMilkOptionCode(value)) {
    return value
  }
  if (typeof value === 'string') {
    return LEGACY_MILK_OPTION_CODE_ALIASES[value] ?? null
  }
  return null
}

/**
 * Human labels for milk option codes. Legacy alias codes collapse to the same
 * label as their canonical form.
 */
export const MILK_OPTION_LABELS: Record<string, string> = {
  '0_REGULAR': '0% Regular (8 oz)',
  '1_REGULAR': '1% Regular (8 oz)',
  '0_CHOCOLATE': '0% Chocolate (8 oz)',
  '1_CHOCOLATE': '1% Chocolate (8 oz)',
  '1_LACTOSE_FREE_8OZ': '1% Lactose Free (8 oz)',
  '1_LACTOSE_FREE_6OZ': '1% Lactose Free (6 oz)',
  WHOLE_MILK_8OZ: 'Whole Milk (8 oz)',
  WHOLE_MILK_6OZ: 'Whole Milk (6 oz)',
  OAT_8OZ: 'Oat Milk (8 oz)',
  SOY_8OZ: 'Soy Milk (8 oz)',
  SOY_6OZ: 'Soy Milk (6 oz)',
  ALMOND_8OZ: 'Almond Milk (8 oz)',
  PEA_8OZ: 'Pea Milk (8 oz)',
  WHOLE_MILK: 'Whole Milk (6 oz)',
  REGULAR_0: '0% Regular (8 oz)',
  REGULAR_1: '1% Regular (8 oz)',
  CHOCOLATE_0: '0% Chocolate (8 oz)',
  CHOCOLATE_1: '1% Chocolate (8 oz)',
  '1_LACTAID': '1% Lactose Free (8 oz)',
  LACTAID_1: '1% Lactose Free (8 oz)',
}

/** Title-case a raw code as a last-resort label (`FOO_BAR` -> `Foo Bar`). */
function formatMilkOptionCode(code: string): string {
  return code
    .toLowerCase()
    .split('_')
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/**
 * Codes no longer offered as a new choice in milk-profile pickers: the 6 oz
 * pours were retired in favour of 8 oz cartons. They stay canonical so stored
 * profiles, menus, orders and SKUs that still carry them keep resolving.
 */
export const RETIRED_MILK_OPTION_CODES = [
  '1_LACTOSE_FREE_6OZ',
  'WHOLE_MILK_6OZ',
  'SOY_6OZ',
] as const satisfies readonly CanonicalMilkOptionCode[]

/** True when `code` (or the canonical form of a legacy alias) is retired from pickers. */
export function isRetiredMilkOptionCode(code: string): boolean {
  const canonical = normalizeMilkOptionCode(code)
  return canonical !== null && (RETIRED_MILK_OPTION_CODES as readonly string[]).includes(canonical)
}

/**
 * The canonical codes a milk picker should render: every code that is not
 * retired, plus any retired code already in `selected` (legacy aliases resolve
 * through their canonical form) so a selection still on it can be unchecked
 * rather than silently kept.
 */
export function selectableMilkOptionCodes(selected: readonly string[] = []): CanonicalMilkOptionCode[] {
  const selectedCanonical = new Set(selected.map((code) => normalizeMilkOptionCode(code)))
  return CANONICAL_MILK_OPTION_CODES.filter((code) => !isRetiredMilkOptionCode(code) || selectedCanonical.has(code))
}

/** Display label for a milk option code, falling back to a formatted code. */
export function milkOptionLabel(code: string): string {
  return MILK_OPTION_LABELS[code] ?? formatMilkOptionCode(code)
}

/**
 * The configured milk option codes in canonical order, deduped, with any
 * non-canonical extras appended in their original order. Blank / whitespace
 * codes drop out. Mirrors `menu-builder.milkComplianceFilterOptions` ordering.
 * Legacy alias codes pass through UNCHANGED (as extras) — callers use the
 * returned codes as lookup keys into raw stored data, so respelling them here
 * would orphan those lookups; use `normalizeMilkOptionCode` at write/read
 * boundaries instead.
 */
export function orderedMilkOptionCodes(codes: readonly string[]): string[] {
  const configured = codes.filter((code) => typeof code === 'string' && code.trim().length > 0)
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const code of CANONICAL_MILK_OPTION_CODES) {
    if (configured.includes(code) && !seen.has(code)) {
      seen.add(code)
      ordered.push(code)
    }
  }
  for (const code of configured) {
    if (!seen.has(code)) {
      seen.add(code)
      ordered.push(code)
    }
  }
  return ordered
}

/**
 * Carton size, in fluid ounces, behind each canonical milk option code. The
 * size lives in the LABEL for humans; this is the same fact for the crediting
 * engine. 8 oz is the K-12 carton; 6 oz is the preschool / CACFP bulk-service
 * pour, which is why only the varieties served at both sizes carry a size
 * suffix in their code.
 */
export const MILK_OPTION_FLUID_OUNCES: Record<CanonicalMilkOptionCode, number> = {
  '0_REGULAR': 8,
  '1_REGULAR': 8,
  '0_CHOCOLATE': 8,
  '1_CHOCOLATE': 8,
  '1_LACTOSE_FREE_8OZ': 8,
  '1_LACTOSE_FREE_6OZ': 6,
  WHOLE_MILK_8OZ: 8,
  WHOLE_MILK_6OZ: 6,
  OAT_8OZ: 8,
  SOY_8OZ: 8,
  SOY_6OZ: 6,
  ALMOND_8OZ: 8,
  PEA_8OZ: 8,
}

/** The USDA meal-pattern milk unit: 1 cup = 8 fl oz (7 CFR 210.10(d)). */
export const FLUID_OUNCES_PER_MILK_CUP = 8

/**
 * Fluid ounces per carton for a milk option code (legacy aliases resolve
 * through their canonical form). Null for a code this vocabulary does not
 * know — callers decide the fallback; never guess a size here.
 */
export function milkOptionFluidOunces(code: string): number | null {
  const canonical = normalizeMilkOptionCode(code)
  return canonical ? MILK_OPTION_FLUID_OUNCES[canonical] : null
}

/**
 * Cups of the fluid-milk component ONE carton of this option credits under
 * the meal pattern: 8 oz → 1 cup, 6 oz → 0.75 cup. Null for an unknown code.
 */
export function milkOptionCreditCups(code: string): number | null {
  if (isNonDairyMilkOptionCode(code)) {
    return null
  }
  const fluidOunces = milkOptionFluidOunces(code)
  return fluidOunces === null ? null : fluidOunces / FLUID_OUNCES_PER_MILK_CUP
}

/** Type/volume is not evidence of nutritional equivalence or an approved accommodation. */
export const NON_DAIRY_MILK_OPTION_CODES = [
  'OAT_8OZ',
  'SOY_8OZ',
  'SOY_6OZ',
  'ALMOND_8OZ',
  'PEA_8OZ',
] as const satisfies readonly CanonicalMilkOptionCode[]

export function isNonDairyMilkOptionCode(code: unknown): boolean {
  const canonical = normalizeMilkOptionCode(code)
  return canonical !== null && (NON_DAIRY_MILK_OPTION_CODES as readonly string[]).includes(canonical)
}
